import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  onSnapshot, 
  collection, 
  query, 
  where, 
  getDocs,
  runTransaction,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { 
  CoachAvailabilitySession, 
  WaitingRoom, 
  WaitingCustomer, 
  CoachAvailabilityStatus,
  CustomerWaitingInfo,
  AvailabilityUpdate,
  AttendedCustomer
} from '@/types/availability';
import { format } from 'date-fns';


/**
 * Why a customer may not join a waiting room, or null when they may (#51).
 * There is no capacity limit — the sixth, or the sixtieth, joins like the
 * first — only the same person twice is refused.
 */
export function waitingRoomRefusal(customers: { customerId: string }[] | undefined, customerId: string): string | null {
  return (customers || []).some((entry) => entry.customerId === customerId) ? 'Customer already in queue' : null;
}

export class AvailabilityService {
  
  /**
   * Set coach availability status for today
   */
  static async setCoachAvailability(
    coachId: string, 
    status: CoachAvailabilityStatus,
    settings?: Partial<CoachAvailabilitySession['settings']>
  ): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const sessionId = `${coachId}-${today}`;
    const sessionRef = doc(db, 'coachAvailabilitySessions', sessionId);
    
    try {
      const existingSession = await getDoc(sessionRef);
      
      if (existingSession.exists()) {
        // Update existing session
        await updateDoc(sessionRef, {
          status,
          lastActivity: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(settings && { settings: { ...existingSession.data().settings, ...settings } })
        });
      } else {
        // Create new session
        const newSession: CoachAvailabilitySession = {
          id: sessionId,
          coachId,
          coachName: '', // Will be populated from user data
          date: today,
          status,
          settings: {
            maxQueueSize: 5,
            averageCallDuration: 15,
            breakBetweenCalls: 2,
            autoBreakAfterCalls: 4,
            ...settings
          },
          stats: {
            callsCompleted: 0,
            totalCallDuration: 0,
            averageWaitTime: 0,
            customersServed: 0
          },
          lastActivity: serverTimestamp() as Timestamp,
          createdAt: serverTimestamp() as Timestamp,
          updatedAt: serverTimestamp() as Timestamp
        };
        
        await setDoc(sessionRef, newSession);
      }
      
      // Also update the waiting room status
      await this.updateWaitingRoomStatus(coachId, today, status);
      
    } catch (error) {
      console.error('Error setting coach availability:', error);
      throw error;
    }
  }
  
  /**
   * Get coach availability status
   */
  static async getCoachAvailability(coachId: string): Promise<CoachAvailabilitySession | null> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const sessionId = `${coachId}-${today}`;
    
    try {
      const sessionDoc = await getDoc(doc(db, 'coachAvailabilitySessions', sessionId));
      return sessionDoc.exists() ? sessionDoc.data() as CoachAvailabilitySession : null;
    } catch (error) {
      console.error('Error getting coach availability:', error);
      return null;
    }
  }
  
  /**
   * Add customer to waiting room
   */
  static async joinWaitingRoom(
    coachId: string, 
    customerId: string, 
    customerName: string,
    customerPhone?: string
  ): Promise<CustomerWaitingInfo> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    return await runTransaction(db, async (transaction) => {
      const waitingRoomDoc = await transaction.get(waitingRoomRef);
      const coachSession = await this.getCoachAvailability(coachId);
      
      // Only allow joining when clinic is open
      if (!coachSession || coachSession.status === 'closed') {
        throw new Error('Coach is offline');
      }
      
      let waitingRoom: WaitingRoom;
      
      if (waitingRoomDoc.exists()) {
        const data = waitingRoomDoc.data();
        // Handle backwards compatibility - old data has 'queue', new data has 'customers'
        waitingRoom = {
          ...data,
          customers: data.customers || data.queue || [],
        } as WaitingRoom;
        
        // No capacity limit (#51): the waiting room has none, so the old link
        // path does not either. Only a second entry for the same person is refused.
        const refusal = waitingRoomRefusal(waitingRoom.customers, customerId);
        if (refusal) throw new Error(refusal);
      } else {
        // Create new waiting room
        waitingRoom = {
          id: waitingRoomId,
          coachId,
          date: today,
          customers: [],
          activeCall: null,
          attendedCustomers: [],
          coachStatus: coachSession.status,
          lastUpdated: serverTimestamp() as Timestamp
        };
      }
      
      // Add customer to waiting room
      const customerEntry: WaitingCustomer = {
        id: `${customerId}-${Date.now()}`,
        customerId,
        customerName,
        customerPhone,
        joinTime: Timestamp.now()
      };
      
      if (!waitingRoom.customers) {
        waitingRoom.customers = [];
      }
      waitingRoom.customers.push(customerEntry);
      waitingRoom.lastUpdated = serverTimestamp() as Timestamp;
      
      transaction.set(waitingRoomRef, waitingRoom);
      
      return {
        isInRoom: true,
        joinTime: customerEntry.joinTime,
        coachStatus: waitingRoom.coachStatus,
        customersCount: waitingRoom.customers.length
      };
    });
  }
  
  /**
   * @deprecated Use joinWaitingRoom instead
   */
  static async joinQueue(
    coachId: string, 
    customerId: string, 
    customerName: string,
    customerPhone?: string
  ): Promise<CustomerWaitingInfo> {
    return this.joinWaitingRoom(coachId, customerId, customerName, customerPhone);
  }
  
  /**
   * Remove customer from waiting room
   */
  static async leaveQueue(coachId: string, customerId: string): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    await runTransaction(db, async (transaction) => {
      const waitingRoomDoc = await transaction.get(waitingRoomRef);
      
      if (!waitingRoomDoc.exists()) {
        return; // Nothing to remove
      }
      
      const data = waitingRoomDoc.data();
      // Handle backwards compatibility - old data has 'queue', new data has 'customers'
      const customers = data.customers || data.queue || [];
      const customerIndex = customers.findIndex((entry: WaitingCustomer) => entry.customerId === customerId);
      
      if (customerIndex === -1) {
        return; // Customer not in room
      }
      
      // Remove customer and save as 'customers' (new format)
      const updatedCustomers = customers.filter((entry: WaitingCustomer) => entry.customerId !== customerId);
      transaction.set(waitingRoomRef, {
        ...data,
        customers: updatedCustomers,
        queue: undefined, // Remove old queue property
        lastUpdated: serverTimestamp()
      });
    });
  }

  /**
   * Remove stale entries (customers who joined more than X minutes ago)
   * This helps clean up entries from customers who closed their browser without leaving properly
   */
  static async cleanupStaleEntries(coachId: string, maxAgeMinutes: number = 60): Promise<number> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    return await runTransaction(db, async (transaction) => {
      const waitingRoomDoc = await transaction.get(waitingRoomRef);
      
      if (!waitingRoomDoc.exists()) {
        return 0;
      }
      
      const data = waitingRoomDoc.data();
      const now = new Date();
      const cutoffTime = new Date(now.getTime() - maxAgeMinutes * 60 * 1000);
      
      // Handle backwards compatibility - old data has 'queue', new data has 'customers'
      const customers = data.customers || data.queue || [];
      const originalLength = customers.length;
      
      // Filter out stale entries
      const updatedCustomers = customers.filter((entry: WaitingCustomer) => {
        const joinTime = entry.joinTime.toDate();
        return joinTime > cutoffTime;
      });
      
      const removedCount = originalLength - updatedCustomers.length;
      
      if (removedCount > 0) {
        transaction.set(waitingRoomRef, {
          ...data,
          customers: updatedCustomers,
          queue: undefined, // Remove old queue property
          lastUpdated: serverTimestamp()
        });
      }
      
      return removedCount;
    });
  }

  /**
   * Remove a specific customer from waiting room
   */
  static async removeCustomerFromWaitingRoom(coachId: string, customerId: string): Promise<void> {
    return this.leaveQueue(coachId, customerId);
  }

  /**
   * Update customer's lastSeen timestamp (heartbeat)
   * This should be called periodically while the customer is on the waiting room page
   */
  static async updateCustomerHeartbeat(coachId: string, customerId: string): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    await runTransaction(db, async (transaction) => {
      const waitingRoomDoc = await transaction.get(waitingRoomRef);
      
      if (!waitingRoomDoc.exists()) return;
      
      const data = waitingRoomDoc.data();
      const customers = data.customers || data.queue || [];
      
      // Find and update the customer's lastSeen
      const updatedCustomers = customers.map((c: WaitingCustomer) => {
        if (c.customerId === customerId) {
          return { ...c, lastSeen: Timestamp.now() };
        }
        return c;
      });
      
      transaction.set(waitingRoomRef, {
        ...data,
        customers: updatedCustomers,
        queue: undefined,
        lastUpdated: serverTimestamp()
      });
    });
  }

  /**
   * Remove customers who haven't sent a heartbeat in the last X seconds
   */
  static async removeInactiveCustomers(coachId: string, maxInactiveSeconds: number = 30): Promise<number> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    return await runTransaction(db, async (transaction) => {
      const waitingRoomDoc = await transaction.get(waitingRoomRef);
      
      if (!waitingRoomDoc.exists()) return 0;
      
      const data = waitingRoomDoc.data();
      const customers = data.customers || data.queue || [];
      const now = Date.now();
      const cutoffTime = now - (maxInactiveSeconds * 1000);
      
      const originalLength = customers.length;
      
      // Filter out inactive customers
      const activeCustomers = customers.filter((c: WaitingCustomer) => {
        const lastSeen = c.lastSeen?.toDate?.()?.getTime() || c.joinTime?.toDate?.()?.getTime() || 0;
        return lastSeen > cutoffTime;
      });
      
      const removedCount = originalLength - activeCustomers.length;
      
      if (removedCount > 0) {
        transaction.set(waitingRoomRef, {
          ...data,
          customers: activeCustomers,
          queue: undefined,
          lastUpdated: serverTimestamp()
        });
      }
      
      return removedCount;
    });
  }
  
  /**
   * Set active call in Firestore (used when starting a call from RTDB presence)
   */
  static async setActiveCall(
    coachId: string, 
    callInfo: {
      customerId: string;
      customerName: string;
      customerPhone?: string;
      roomId: string;
      /** The called customer's presence joinTime, when the call comes from the waiting room. */
      joinTime?: Timestamp | null;
    }
  ): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    // callId (+ the called presence session's joinTime) let the beneficiary
    // recognise "this call is for me, now" by identity instead of comparing
    // two devices' clocks (activeCallAcceptance.ts; mobile clinicCallSignal.ts).
    // startTime is server time for older clients' 60 s check and the
    // stale-call banners (#47). Same shape as namat-mobile setActiveCall.
    const { joinTime, ...info } = callInfo;
    const activeCall = {
      ...info,
      callId: callInfo.roomId,
      ...(joinTime ? { joinTime } : {}),
      startTime: serverTimestamp()
    };
    
    const waitingRoomDoc = await getDoc(waitingRoomRef);
    
    if (waitingRoomDoc.exists()) {
      await updateDoc(waitingRoomRef, {
        activeCall,
        lastUpdated: serverTimestamp()
      });
    } else {
      // Create waiting room with active call
      await setDoc(waitingRoomRef, {
        id: waitingRoomId,
        coachId,
        date: today,
        customers: [],
        activeCall,
        attendedCustomers: [],
        coachStatus: 'open',
        lastUpdated: serverTimestamp()
      });
    }
  }

  /**
   * Start call with a customer from the waiting room (Firestore-based)
   * @deprecated Use setActiveCall with RTDB presence instead
   */
  static async startCallWithCustomer(coachId: string, customerId: string): Promise<{ roomId: string; customer: WaitingCustomer } | null> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    return await runTransaction(db, async (transaction) => {
      const waitingRoomDoc = await transaction.get(waitingRoomRef);
      
      if (!waitingRoomDoc.exists()) {
        return null;
      }
      
      const data = waitingRoomDoc.data();
      // Handle backwards compatibility - old data has 'queue', new data has 'customers'
      const customers = [...(data.customers || data.queue || [])];
      
      if (customers.length === 0) {
        return null; // No customers waiting
      }
      
      // Find the customer to call
      const customerIndex = customers.findIndex((c: WaitingCustomer) => c.customerId === customerId);
      if (customerIndex === -1) {
        return null; // Customer not found in room
      }
      
      // Remove the customer from waiting room
      const [selectedCustomer] = customers.splice(customerIndex, 1);
      const roomId = `availability-${coachId}-${selectedCustomer.customerId}-${Date.now()}`;
      
      // Set active call and save with new 'customers' format
      transaction.set(waitingRoomRef, {
        ...data,
        customers: customers,
        queue: undefined, // Remove old queue property
        activeCall: {
          customerId: selectedCustomer.customerId,
          customerName: selectedCustomer.customerName,
          customerPhone: selectedCustomer.customerPhone,
          roomId,
          startTime: Timestamp.now()
        },
        lastUpdated: serverTimestamp()
      });
      
      return { roomId, customer: selectedCustomer as WaitingCustomer };
    });
  }
  
  /**
   * End current call and prepare for next
   */
  static async endCurrentCall(coachId: string, callDuration: number): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const sessionId = `${coachId}-${today}`;
    
    await runTransaction(db, async (transaction) => {
      const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
      const sessionRef = doc(db, 'coachAvailabilitySessions', sessionId);
      
      const [waitingRoomDoc, sessionDoc] = await Promise.all([
        transaction.get(waitingRoomRef),
        transaction.get(sessionRef)
      ]);
      
      // Update waiting room
      if (waitingRoomDoc.exists()) {
        const waitingRoom = waitingRoomDoc.data() as WaitingRoom;
        
        // Store the attended customer before clearing activeCall
        if (waitingRoom.activeCall) {
          const attendedCustomer: AttendedCustomer = {
            customerId: waitingRoom.activeCall.customerId,
            customerName: waitingRoom.activeCall.customerName,
            customerPhone: waitingRoom.activeCall.customerPhone,
            callStartTime: waitingRoom.activeCall.startTime,
            callEndTime: Timestamp.now(),
            callDuration
          };
          
          // Initialize attendedCustomers array if it doesn't exist
          if (!waitingRoom.attendedCustomers) {
            waitingRoom.attendedCustomers = [];
          }
          
          waitingRoom.attendedCustomers.push(attendedCustomer);
        }
        
        waitingRoom.activeCall = null;
        waitingRoom.lastUpdated = serverTimestamp() as Timestamp;
        transaction.set(waitingRoomRef, waitingRoom);
      }
      
      // Update session stats
      if (sessionDoc.exists()) {
        const session = sessionDoc.data() as CoachAvailabilitySession;
        session.stats.callsCompleted += 1;
        session.stats.totalCallDuration += callDuration;
        session.stats.customersServed += 1;
        session.lastActivity = serverTimestamp() as Timestamp;
        session.updatedAt = serverTimestamp() as Timestamp;
        transaction.set(sessionRef, session);
      }
    });
  }
  
  /**
   * Clear the active call WITHOUT recording attendance or stats — for a call
   * that never connected (the other party never joined) or a stale call (#47).
   * Only clears it if it is still the same room, so a newer call is untouched.
   */
  static async cancelActiveCall(coachId: string, roomId?: string): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomRef = doc(db, 'waitingRooms', `${coachId}-${today}`);

    await runTransaction(db, async (transaction) => {
      const waitingRoomDoc = await transaction.get(waitingRoomRef);
      if (!waitingRoomDoc.exists()) return;
      const activeCall = (waitingRoomDoc.data() as WaitingRoom).activeCall;
      if (!activeCall) return;
      if (roomId && activeCall.roomId !== roomId) return;
      transaction.update(waitingRoomRef, {
        activeCall: null,
        lastUpdated: serverTimestamp()
      });
    });
  }

  /**
   * Listen to waiting room updates
   */
  static listenToWaitingRoom(
    coachId: string, 
    callback: (waitingRoom: WaitingRoom | null) => void
  ): () => void {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    return onSnapshot(waitingRoomRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        // Handle backwards compatibility - old data has 'queue', new data has 'customers'
        const waitingRoom: WaitingRoom = {
          ...data,
          customers: data.customers || data.queue || [],
        } as WaitingRoom;
        callback(waitingRoom);
      } else {
        callback(null);
      }
    });
  }
  
  /**
   * Listen to customer's status in the waiting room
   */
  static listenToCustomerStatus(
    coachId: string,
    customerId: string,
    callback: (info: CustomerWaitingInfo | null) => void
  ): () => void {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    return onSnapshot(waitingRoomRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        // Handle backwards compatibility - old data has 'queue', new data has 'customers'
        const customers = data.customers || data.queue || [];
        const customerEntry = customers.find((entry: WaitingCustomer) => entry.customerId === customerId);
        
        if (customerEntry) {
          callback({
            isInRoom: true,
            joinTime: customerEntry.joinTime,
            coachStatus: data.coachStatus,
            customersCount: customers.length
          });
        } else {
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  }
  
  /**
   * @deprecated Use listenToCustomerStatus instead
   */
  static listenToQueuePosition(
    coachId: string,
    customerId: string,
    callback: (info: CustomerWaitingInfo | null) => void
  ): () => void {
    return this.listenToCustomerStatus(coachId, customerId, callback);
  }
  
  /**
   * Get available coaches (coaches who are currently available/open)
   */
  static async getAvailableCoaches(): Promise<AvailabilityUpdate[]> {
    const today = format(new Date(), 'yyyy-MM-dd');
    
    try {
      // Query all open clinics for today
      const sessionsQuery = query(
        collection(db, 'coachAvailabilitySessions'),
        where('date', '==', today),
        where('status', '==', 'open')
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      const availableCoaches: AvailabilityUpdate[] = [];
      
      for (const sessionDoc of sessionsSnapshot.docs) {
        const session = sessionDoc.data() as CoachAvailabilitySession;
        
        // Get coach info
        const coachDoc = await getDoc(doc(db, 'users', session.coachId));
        if (coachDoc.exists()) {
          const coachData = coachDoc.data();
          availableCoaches.push({
            coachId: session.coachId,
            coachName: coachData.firstNameEn 
              ? `${coachData.firstNameEn} ${coachData.lastNameEn || ''}`.trim()
              : `${coachData.firstNameAr || ''} ${coachData.lastNameAr || ''}`.trim(),
            coachNameAr: `${coachData.firstNameAr || ''} ${coachData.lastNameAr || ''}`.trim(),
            coachNameEn: `${coachData.firstNameEn || ''} ${coachData.lastNameEn || ''}`.trim(),
            status: session.status,
            customersCount: 0, // Will be populated from waiting room
            lastUpdated: session.lastActivity
          });
        }
      }
      
      // Get customer counts from waiting rooms
      for (const coach of availableCoaches) {
        const waitingRoomId = `${coach.coachId}-${today}`;
        const waitingRoomDoc = await getDoc(doc(db, 'waitingRooms', waitingRoomId));
        if (waitingRoomDoc.exists()) {
          const data = waitingRoomDoc.data();
          coach.customersCount = (data.customers || data.queue || []).length;
        }
      }
      
      return availableCoaches;
    } catch (error) {
      console.error('Error getting available coaches:', error);
      return [];
    }
  }

  /**
   * Listen to open clinics in real-time
   */
  static listenToOpenClinics(
    callback: (coaches: AvailabilityUpdate[]) => void
  ): () => void {
    const today = format(new Date(), 'yyyy-MM-dd');
    
    const sessionsQuery = query(
      collection(db, 'coachAvailabilitySessions'),
      where('date', '==', today),
      where('status', '==', 'open')
    );
    
    return onSnapshot(sessionsQuery, async (snapshot) => {
      const availableCoaches: AvailabilityUpdate[] = [];
      
      for (const sessionDoc of snapshot.docs) {
        const session = sessionDoc.data() as CoachAvailabilitySession;
        
        // Get coach info
        const coachDoc = await getDoc(doc(db, 'users', session.coachId));
        if (coachDoc.exists()) {
          const coachData = coachDoc.data();
          
          // Get customer count from waiting room
          const waitingRoomId = `${session.coachId}-${today}`;
          const waitingRoomDoc = await getDoc(doc(db, 'waitingRooms', waitingRoomId));
          const customersCount = waitingRoomDoc.exists() 
            ? (waitingRoomDoc.data().customers || waitingRoomDoc.data().queue || []).length 
            : 0;
          
          availableCoaches.push({
            coachId: session.coachId,
            coachName: coachData.firstNameEn 
              ? `${coachData.firstNameEn} ${coachData.lastNameEn || ''}`.trim()
              : `${coachData.firstNameAr || ''} ${coachData.lastNameAr || ''}`.trim(),
            coachNameAr: `${coachData.firstNameAr || ''} ${coachData.lastNameAr || ''}`.trim(),
            coachNameEn: `${coachData.firstNameEn || ''} ${coachData.lastNameEn || ''}`.trim(),
            status: session.status,
            customersCount,
            lastUpdated: session.lastActivity
          });
        }
      }
      
      callback(availableCoaches);
    });
  }
  
  // Private helper methods
  
  private static async updateWaitingRoomStatus(
    coachId: string, 
    date: string, 
    status: CoachAvailabilityStatus
  ): Promise<void> {
    const waitingRoomId = `${coachId}-${date}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    
    try {
      const waitingRoomDoc = await getDoc(waitingRoomRef);
      
      if (waitingRoomDoc.exists()) {
        await updateDoc(waitingRoomRef, {
          coachStatus: status,
          lastUpdated: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating waiting room status:', error);
    }
  }
}
